import React from "react";
import { Capacitor } from "@capacitor/core";
import { IonBackButton, IonButtons, IonContent, IonHeader, IonPage, IonToolbar } from "@ionic/react";
import { useParams, withRouter } from "react-router";
import HeaderTitle from "@components/common/HeaderTitle";
import DataController from "@controllers/DataController";
import * as ProgramUtils from "@utils/programUtils";
import { useProgramData } from "@hooks/data/useProgramData";
import { useHidingTabBar } from "@hooks/tabs/useHidingTabBar";
import { useProgramDetailsState } from "@hooks/programDetails/useProgramDetailsState";
import { useProgramDetailsValidation } from "@hooks/programDetails/useProgramDetailsValidation";
import { useProgramDetailsSave } from "@hooks/programDetails/useProgramDetailsSave";
import ProgramDetailsFooter from "@components/programDetails/ProgramDetailsFooter";
import DeclarationSection from "@components/programDetails/DeclarationSection";
import RoleSelectionSection from "@components/programDetails/RoleSelectionSection";
import AreaTeamSelection from "@components/programDetails/AreaTeamSelection";
import OptionalStandardsSection from "@components/programDetails/OptionalStandardsSection";
import CertificateManagementSection from "@components/programDetails/CertificateManagementSection";
import StartDateSelection from "@components/programDetails/StartDateSelection";
import NQPCourseSelection from "./NQPCourseSelection";
import ContentContainer from "@components/common/ContentContainer";

const ProgramDetails: React.FC = () => {
  const params = useParams<{ programId: string }>();

  useHidingTabBar();
  const { userPrograms } = useProgramData();
  const program = userPrograms.find((item) => item.ID === params.programId)!;

  const state = useProgramDetailsState(program);
  const { canSaveDetails, optionalStandardsForSelectedRole } = useProgramDetailsValidation(program, state);
  const { handleSaveDetails } = useProgramDetailsSave(program, state.setSaving, state.setUser);

  const saveDetails = async (): Promise<void> => {
    if (state.user.state === "hasValue" && state.user.contents) {
      await handleSaveDetails(
        state.user.contents,
        state.selectedPrimaryRoles,
        state.selectedSecondaryRoles,
        state.selectedStandards,
        state.selectedArea,
        state.selectedTeam,
        state.appraisalDate,
        state.uploads,
        state.deletions,
        state.issueDates,
        state.expiryDates,
        state.nqpCourse,
        state.startDate,
        state.complianceData
      );
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${params.programId}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Your Details"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background whiteBackground">
        <ContentContainer
          className="!pt-0 pb-4"
          style={{ paddingTop: 0, alignSelf: "center" }}
        >
          <div className="mx-auto max-w-app-column pt-4">
            <div className="programAddCertificateTitle">{"Set up your details"}</div>
            {program?.DetailsDescription && <div className="accountCardSubTitle">{program.DetailsDescription}</div>}
            <RoleSelectionSection
              program={program}
              allRoles={state.allRoles}
              selectedPrimaryRoles={state.selectedPrimaryRoles}
              selectedSecondaryRoles={state.selectedSecondaryRoles}
              onPrimaryRoleChange={state.setSelectedPrimaryRoles}
              onSecondaryRolesChange={state.setSelectedSecondaryRoles}
              onTeamReset={() => state.setSelectedTeam("")}
              onAreaReset={() => state.setSelectedArea("")}
              onStandardsReset={() => state.setSelectedStandards([])}
            />
            {ProgramUtils.showCertsAfterRoleSelection(program?.ID) && (
              <div className="mb-4">
                <CertificateManagementSection
                  program={program}
                  allCertificates={state.allCertificates}
                  uploads={state.uploads}
                  deletions={state.deletions}
                  existingCertificates={state.existingCertificates}
                  issueDates={state.issueDates}
                  expiryDates={state.expiryDates}
                  certificateType={state.certificateType}
                  selectingCertificate={state.selectingCertificate}
                  deletingCertificate={state.deletingCertificate}
                  datePickerVisible={state.datePickerVisible}
                  datePickerType={state.datePickerType}
                  datePickerValue={state.datePickerValue}
                  newDateInfo={state.newDateInfo}
                  changingDate={state.changingDate}
                  user={state.user}
                  setAllCertificates={state.setAllCertificates}
                  setUploads={state.setUploads}
                  setDeletions={state.setDeletions}
                  setExistingCertificates={state.setExistingCertificates}
                  setIssueDates={state.setIssueDates}
                  setExpiryDates={state.setExpiryDates}
                  setCertificateType={state.setCertificateType}
                  setSelectingCertificate={state.setSelectingCertificate}
                  setDeletingCertificate={state.setDeletingCertificate}
                  setDatePickerVisible={state.setDatePickerVisible}
                  setDatePickerType={state.setDatePickerType}
                  setDatePickerValue={state.setDatePickerValue}
                  setNewDateInfo={state.setNewDateInfo}
                  setChangingDate={state.setChangingDate}
                />
              </div>
            )}
            <AreaTeamSelection
              program={program}
              selectedArea={state.selectedArea}
              selectedTeam={state.selectedTeam}
              onAreaChange={state.setSelectedArea}
              onTeamChange={state.setSelectedTeam}
              selectedPrimaryRoles={state.selectedPrimaryRoles}
            />
            <StartDateSelection
              program={program}
              startDate={state.startDate}
              startDatePickerVisible={state.startDatePickerVisible}
              onStartDateChange={state.setStartDate}
              onStartDatePickerVisibilityChange={state.setStartDatePickerVisible}
            />
            <NQPCourseSelection
              program={program}
              nqpCourse={state.nqpCourse}
              onNQPCourseChange={state.setNQPCourse}
            />
            <OptionalStandardsSection
              program={program}
              standardsForSelectedRole={optionalStandardsForSelectedRole}
              selectedStandards={state.selectedStandards}
              onStandardsChange={state.setSelectedStandards}
            />
            <DeclarationSection
              program={program}
              declarationChecked={state.declarationChecked}
              onDeclarationChange={state.setDeclarationChecked}
            />
          </div>
        </ContentContainer>
      </IonContent>
      <ProgramDetailsFooter
        canSaveDetails={canSaveDetails}
        saving={state.saving}
        onSave={saveDetails}
      />
    </IonPage>
  );
};

export default withRouter(ProgramDetails);
